import { useState } from "react";
import { Trash, Plus, CheckCircle, XCircle } from "@phosphor-icons/react";
import { act, api, useFetch } from "@/lib/api";
import { Panel, Badge } from "@/components/common";
import { FixGuide } from "@/components/FixGuide";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const Tick = ({ ok, label, id }) => (
  <span className={`inline-flex items-center gap-1 ${ok ? "text-emerald-400" : "text-red-400"}`} data-testid={id}>{ok ? <CheckCircle weight="fill" /> : <XCircle weight="fill" />}{label}</span>
);

const Health = () => {
  const { data } = useFetch("/senders/health");
  if (!data) return null;
  return (
    <div className="mt-4 space-y-2 text-xs" data-testid="senders-health">
      <div className="text-zinc-300" data-testid="senders-capacity">
        {data.mailboxes} mailbox{data.mailboxes === 1 ? "" : "es"} · today up to <b className="text-white">{data.today_capacity}</b> cold emails · at full warm-up {data.full_capacity}/day.
        {" "}5,000 a day needs about {data.mailboxes_for_5000} mailboxes at {data.per_mailbox_max} each.
      </div>
      {data.domains.map((d) => (
        <div key={d.domain} className="flex flex-wrap gap-3 items-center" data-testid={`domain-health-${d.domain}`}>
          <span className="text-white w-40 truncate">{d.domain}</span>
          <Tick ok={d.mx} label="MX" id={`dns-mx-${d.domain}`} /><Tick ok={d.spf} label="SPF" id={`dns-spf-${d.domain}`} />
          <Tick ok={d.dkim} label="DKIM" id={`dns-dkim-${d.domain}`} /><Tick ok={d.dmarc} label="DMARC" id={`dns-dmarc-${d.domain}`} />
        </div>
      ))}
    </div>
  );
};

export const SendersPanel = ({ isAdmin }) => {
  const { data, reload } = useFetch("/senders");
  const [emails, setEmails] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [guide, setGuide] = useState(false);
  const add = async () => {
    setBusy(true);
    const r = await act(() => api.post("/senders", { emails }));
    setBusy(false);
    if (r) { setRes(r); setEmails(""); reload(); }
  };
  const remove = async (e) => {
    if (!window.confirm(`Stop sending from ${e}? Its contacts move to the other mailboxes.`)) return;
    if (await act(() => api.delete(`/senders/${encodeURIComponent(e)}`), `${e} removed`)) reload();
  };
  if (!data) return null;
  return (
    <Panel title="Cold-email sending mailboxes" data-testid="senders-panel">
      <p className="text-xs text-zinc-500 mb-3">MaSa rotates cold emails across these mailboxes. Each one warms up by itself from 20 to 50 a day, follow-ups come from the mailbox that sent Email 1, and replies go to the main mailbox. Use separate lookalike domains, never specia.ai.</p>
      <ul className="space-y-1.5 text-sm max-h-56 overflow-auto">
        <li className="flex items-center gap-2" data-testid="sender-primary">{data.primary || "Not connected"} <Badge v="main" tone="blue" /></li>
        {data.extra.map((x) => (
          <li key={x.email} className="flex items-center gap-2" data-testid={`sender-${x.email}`}>
            {x.email} <Badge v="verified" tone="green" />
            {isAdmin && <Button size="sm" variant="ghost" className="ml-auto h-7" data-testid={`sender-remove-${x.email}`} onClick={() => remove(x.email)}><Trash /></Button>}
          </li>
        ))}
      </ul>
      <Health key={data.extra.length} />
      {isAdmin && data.primary && (
        <div className="mt-4 space-y-2">
          <Textarea data-testid="sender-add-input" rows={3} value={emails} onChange={(e) => setEmails(e.target.value)} placeholder="Paste one or many mailboxes, e.g. pawan@getspecia.com, alex@tryspecia.com" className="bg-black text-sm" />
          <Button data-testid="sender-add-btn" disabled={busy || !emails.trim()} onClick={add} className="gap-1.5"><Plus /> {busy ? "Checking each mailbox…" : "Add mailboxes"}</Button>
          {res && (
            <div className="text-xs space-y-1" data-testid="sender-add-result">
              {res.added.length > 0 && <div className="text-emerald-400">Added: {res.added.join(", ")}</div>}
              {res.failed.map((f) => <div key={f.email} className="text-red-400">{f.email}: {f.error}</div>)}
            </div>
          )}
        </div>
      )}
      <Button variant="link" size="sm" className="px-0 mt-2 text-[#F97316]" data-testid="senders-guide-toggle" onClick={() => setGuide(!guide)}>{guide ? "Hide" : "Show"} the setup guide for 5,000 a day</Button>
      {guide && <FixGuide id="cold-domains" guide={data.guide} />}
    </Panel>
  );
};

export const SesPanel = () => {
  const { data } = useFetch("/ses/status");
  const { data: g } = useFetch("/senders");
  const [guide, setGuide] = useState(false);
  if (!data) return null;
  return (
    <Panel title="Opt-in email (Amazon SES)" data-testid="ses-panel">
      {!data.configured ? <p className="text-sm text-zinc-400" data-testid="ses-status">Not connected yet. Newsletters and waitlist emails use the current sender until SES is set up.</p> : data.error ? (
        <p className="text-sm text-red-400" data-testid="ses-status">Connected, but SES returned an error: {data.error}</p>
      ) : (
        <div className="text-sm space-y-1" data-testid="ses-status">
          <div>Sending as <b className="text-white">{data.from}</b></div>
          <div className="flex gap-3 text-xs"><Tick ok={data.verified} label="Domain verified" id="ses-verified" /><Tick ok={data.dkim === "SUCCESS"} label="DKIM" id="ses-dkim" /><Tick ok={!data.sandbox} label={data.sandbox ? "Sandbox (only verified recipients)" : "Production access"} id="ses-production" /></div>
          <div className="text-xs text-zinc-500">{data.sent_24h ?? 0} sent in the last 24h of {data.max_24h ?? "?"} allowed. Every email has one-click unsubscribe.</div>
        </div>
      )}
      <Button variant="link" size="sm" className="px-0 mt-2 text-[#F97316]" data-testid="ses-guide-toggle" onClick={() => setGuide(!guide)}>{guide ? "Hide" : "Show"} the SES setup guide</Button>
      {guide && g && <FixGuide id="ses-setup" guide={g.ses_guide} />}
    </Panel>
  );
};
